import { AttachingMethod, Attachment } from '../../../../types';
import { logger } from '../../../util/logger';
import { isFileExtensionSupported, resolveUploadMimeType } from './constants';
import { convertToJpeg } from './convertToJpeg';
import { MediaDimensionsProbe, MediaProbeResult } from './probeMediaDimensions';

export type BaseUploadUrlResponse = { uploadUrl: string; attachmentUrl: string };

type UploaderConstructor = {
  new (
    file: File,
    attachingMethod: AttachingMethod,
    signal?: AbortSignal,
  ): BaseMediaUploader<BaseUploadUrlResponse>;
  readonly uploaderName: string;
  readonly supportedFileExtensions: readonly string[];
};

export abstract class BaseMediaUploader<TResponse extends BaseUploadUrlResponse> {
  // Overridden by each concrete uploader. Kept as a stable static (not this.constructor.name, which
  // terser mangles in the production bundle) so log/error messages stay readable in prod.
  static readonly uploaderName: string = 'MediaUploader';

  constructor(
    protected readonly file: File,
    protected readonly attachingMethod: AttachingMethod,
    protected readonly signal?: AbortSignal,
  ) {}

  static async upload(
    this: UploaderConstructor,
    files: File[],
    attachingMethod: AttachingMethod,
    signal?: AbortSignal,
  ): Promise<Attachment[]> {
    BaseMediaUploader.assertSupportedFiles(files, this.uploaderName, this.supportedFileExtensions);
    // Convert Chrome-undecodable images (HEIC/HEIF) to JPEG per file, so a slow decode only delays
    // its own upload. The rest of the pipeline then operates on the converted .jpg file.
    return Promise.all(
      files.map(async (file) => {
        const prepared = await convertToJpeg(file);
        return new this(prepared, attachingMethod, signal).run();
      }),
    );
  }

  protected abstract readonly requiredResponseFields: readonly string[];

  protected abstract getUploadUrl(): Promise<TResponse>;
  protected abstract buildAttachment(response: TResponse, probe: MediaProbeResult): Attachment;

  protected assertUploadResponse(response: TResponse): void {
    const missingFields = this.requiredResponseFields.filter((f) => !response[f as keyof TResponse]);
    if (missingFields.length > 0) {
      const uploaderName = (this.constructor as typeof BaseMediaUploader).uploaderName;
      const error = new Error(`[${uploaderName}] BFF response missing field(s): ${missingFields.join(', ')}`);
      logger.error({ message: 'BFF response missing required fields', error, callParams: { missingFields } });
      throw error;
    }
  }

  protected async run(): Promise<Attachment> {
    // Probe starts immediately and runs in parallel with both the BFF handshake and the PUT upload.
    // Results are only needed to build the Attachment after PUT completes, so probe latency is hidden.
    const probePromise = MediaDimensionsProbe.probe(this.file);
    const response = await this.getUploadUrl();
    this.assertUploadResponse(response);
    const [, probe] = await Promise.all([this.putFile(response.uploadUrl), probePromise]);
    return this.buildAttachment(response, probe);
  }

  private async putFile(uploadUrl: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: this.file,
      signal: this.signal,
      headers: { 'Content-Type': resolveUploadMimeType(this.file) },
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  }

  private static assertSupportedFiles(
    files: File[],
    uploaderName: string,
    supportedFileExtensions: readonly string[],
  ): void {
    const unsupported = files
      .filter((f) => !isFileExtensionSupported(f, supportedFileExtensions))
      .map((f) => {
        const dotIndex = f.name.lastIndexOf('.');
        return { name: f.name, extension: dotIndex !== -1 ? f.name.slice(dotIndex + 1).toLowerCase() : '' };
      });

    if (unsupported.length > 0) {
      const parts = unsupported.map(({ name, extension }) => `${name} (.${extension})`).join(', ');
      const error = new Error(`[${uploaderName}] Unsupported file type(s): ${parts}`);
      logger.error({ message: 'unsupported file type(s)', error, callParams: { unsupported } });
      throw error;
    }
  }
}
